import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { LegalRequirementFormModal } from '../features/legalRequirements/LegalRequirementFormModal';
import {
  createLegalRequirement,
  fetchExpiryAlertsPreview,
  fetchLegalRequirements,
} from '../features/legalRequirements/legalRequirementsApi';
import type {
  ComplianceStatus,
  ExpiryAlertPreviewItem,
  LegalReqType,
  LegalRequirementSummary,
} from '../features/legalRequirements/types';
import {
  COMPLIANCE_STATUS_LABEL,
  LEGAL_REQ_TYPE_LABEL,
  complianceStatusBadgeClass,
  expiryStateBadgeClass,
  formatDaysUntilDue,
} from '../features/legalRequirements/legalRequirementsUtils';
import { DateCell } from '../components/DateCell';

const TYPES = Object.keys(LEGAL_REQ_TYPE_LABEL) as LegalReqType[];
const STATUSES = Object.keys(COMPLIANCE_STATUS_LABEL) as ComplianceStatus[];

export function LegalRequirementsPage() {
  const { canEdit } = usePlatformAccessLevel('legal_requirements.access');
  const [items, setItems] = useState<LegalRequirementSummary[]>([]);
  const [alerts, setAlerts] = useState<ExpiryAlertPreviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, preview] = await Promise.all([
        fetchLegalRequirements({
          page,
          pageSize: 20,
          type: filterType || undefined,
          status: filterStatus || undefined,
          search: search.trim() || undefined,
        }),
        canEdit ? fetchExpiryAlertsPreview().catch(() => ({ items: [] })) : Promise.resolve({ items: [] }),
      ]);
      setItems(list.data);
      setTotalPages(list.pagination.pages);
      setAlerts(preview.items ?? []);
    } catch {
      setError('No se pudieron cargar los requisitos legales.');
    } finally {
      setLoading(false);
    }
  }, [page, filterType, filterStatus, search, canEdit]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Requisitos legales</h1>
          <p className="text-muted-foreground text-sm">
            Inventario, cumplimiento y vencimientos (ISO 4.2)
          </p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-primary" onClick={() => setModalOpen(true)}>
            Nuevo requisito
          </button>
        )}
      </div>

      {canEdit && alerts.length > 0 && (
        <section className="bg-card border border-border rounded-lg p-4 space-y-3">
          <h2 className="font-semibold text-sm">Próximos vencimientos</h2>
          <ul className="space-y-2">
            {alerts.map((alert) => (
              <li
                key={alert.requirementId}
                className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-border pb-2 last:border-0"
              >
                <div>
                  <Link
                    to={`/requisitos-legales/${alert.requirementId}`}
                    className="font-medium hover:underline"
                  >
                    {alert.code} — {alert.title}
                  </Link>
                  <p className="text-muted-foreground text-xs">
                    Vence: <DateCell value={alert.expiryDate} />
                  </p>
                </div>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${expiryStateBadgeClass(alert.daysUntilDue)}`}
                >
                  {formatDaysUntilDue(alert.daysUntilDue)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex flex-wrap gap-3">
        <input
          type="search"
          placeholder="Buscar…"
          className="rounded-md border border-border px-3 py-2 text-sm min-w-[200px]"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="rounded-md border border-border px-3 py-2 text-sm"
          value={filterType}
          onChange={(e) => {
            setFilterType(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los tipos</option>
          {TYPES.map((t) => (
            <option key={t} value={t}>
              {LEGAL_REQ_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
        <select
          className="rounded-md border border-border px-3 py-2 text-sm"
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {COMPLIANCE_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}

      {loading ? (
        <p className="text-muted-foreground text-sm">Cargando…</p>
      ) : (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Código</th>
                <th className="text-left p-3 font-medium">Título</th>
                <th className="text-left p-3 font-medium">Tipo</th>
                <th className="text-left p-3 font-medium">Cumplimiento</th>
                <th className="text-left p-3 font-medium">Vencimiento</th>
                <th className="text-left p-3 font-medium">Evaluaciones</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-muted-foreground">
                    No hay requisitos legales registrados.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="border-t border-border hover:bg-muted/30">
                    <td className="p-3 font-mono text-xs">{item.code}</td>
                    <td className="p-3">
                      <Link to={`/requisitos-legales/${item.id}`} className="hover:underline font-medium">
                        {item.title}
                      </Link>
                      {item.processArea && (
                        <p className="text-xs text-muted-foreground">{item.processArea}</p>
                      )}
                    </td>
                    <td className="p-3">{LEGAL_REQ_TYPE_LABEL[item.type]}</td>
                    <td className="p-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${complianceStatusBadgeClass(item.complianceStatus)}`}
                      >
                        {COMPLIANCE_STATUS_LABEL[item.complianceStatus]}
                      </span>
                    </td>
                    <td className="p-3">
                      {item.expiryDate ? <DateCell value={item.expiryDate} /> : '—'}
                    </td>
                    <td className="p-3">{item._count?.evaluations ?? 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center gap-2 justify-center">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </button>
        </div>
      )}

      <LegalRequirementFormModal
        open={modalOpen}
        title="Nuevo requisito legal"
        onClose={() => setModalOpen(false)}
        onSubmit={async (payload) => {
          await createLegalRequirement(payload);
          await load();
        }}
      />
    </div>
  );
}
